import { useEffect, useMemo, useSyncExternalStore } from 'react';
import type { SceneStore } from '@os/doc';
import type { EditorController } from '../editor/controller';
import { locationPageUrl, memoryPageUrl, PageNav, type PageUrl } from './page-nav';

const sessionStore = (): Storage | null => {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
};

/**
 * The open board's page navigation (P14): `?page=` opens its page before the canvas attaches, and every page change,
 * whatever made it, saves the old page's camera and shows the new one through `controller.switchPage`.
 */
/** `url` null: a board shown inside another (a version preview): the page lives in memory, no URL, no saved camera. */
export function usePageNav(store: SceneStore, controller: EditorController, boardId: string | undefined, url: PageUrl | null = locationPageUrl): PageNav {
  const nav = useMemo(
    () => new PageNav({ store, boardId: boardId ?? 'local', storage: url ? sessionStore() : null, url: url ?? memoryPageUrl() }),
    [store, boardId, url],
  );
  useEffect(() => {
    const stop = nav.start();
    let prev = store.page;
    const off = store.subscribePage(() => {
      nav.saveCamera(prev, controller.camera);
      prev = store.page;
      controller.switchPage(nav.cameraFor(prev));
    });
    return () => {
      off();
      stop();
    };
  }, [nav, store, controller]);
  return nav;
}

/** The active page's id, re-rendering on change. */
export const useActivePage = (store: SceneStore): string =>
  useSyncExternalStore(
    (cb) => store.subscribePage(cb),
    () => store.page,
  );
